import { z } from 'zod';
import { MAX_INVESTOR_INDUSTRIES } from '@/lib/constants/industries';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { phoneSchema, toPhoneFormFields } from '@/lib/utils/phone.utils';
import { optionalWholeNumber } from '@/lib/utils/whole-number-schema.utils';
import type { InvestorProfileData } from '../../api/investor-profile.api';
import { COLLEAGUE_EMAIL_MAX_LENGTH, COLLEAGUE_NAME_MAX_LENGTH } from './investor-form.constants';
import { INVESTOR_PREF_LEVEL_OPTIONS, MARKET_MODEL_OPTIONS } from './investor-options';

const COLLEAGUE_ERRORS = UI_STRINGS.INVESTOR_PROFILE.COLLEAGUE_ERRORS;
const E = UI_STRINGS.INVESTOR_PROFILE.ERRORS;

const optionalUrl = z.union([z.string().trim().url(E.URL_INVALID), z.literal('')]).optional();
const optionalText = z.string().trim().optional();
/** A choice made with a select: empty is "not chosen yet" (FR-186). The Backend validates the value. */
const optionalSelect = z.string().trim().optional();

/**
 * A segmented control's value: empty or one of the options. A value outside them has
 * a plain-language message, so a rule that does fail never shows the library's own text.
 */
const choice = (options: readonly { value: string }[]) => {
  const allowed = options.map((option) => option.value);
  return z
    .string()
    .refine((value) => value === '' || allowed.includes(value), E.CHOICE_INVALID)
    .optional();
};

/**
 * Empty, null, undefined and NaN all mean "no value" for an id field. A numeric
 * Controller can hand back `NaN` after a cleared select, and the validation
 * library's default text for it ("Expected number, received nan") must never reach
 * the screen (A-206).
 */
function toNoValue(value: unknown): unknown {
  const isNaNNumber = typeof value === 'number' && Number.isNaN(value);
  return value === '' || value === null || value === undefined || isNaNNumber ? undefined : value;
}

/** Native <select>/Combobox values arrive as raw strings until submit-time coercion. */
const requiredCountryId = z.preprocess(
  toNoValue,
  z.coerce
    .number({ error: E.COUNTRY_REQUIRED })
    .int(E.COUNTRY_REQUIRED)
    .positive(E.COUNTRY_REQUIRED),
);
const optionalStateId = z.preprocess(
  toNoValue,
  z.coerce
    .number({ error: E.STATE_INVALID })
    .int(E.STATE_INVALID)
    .positive(E.STATE_INVALID)
    .optional(),
);

/** A Preferred geographies row needs a region and a country; the state is optional. */
const geographySchema = z.object({
  worldRegion: z.string().min(1, E.REGION_REQUIRED),
  countryId: requiredCountryId,
  stateId: optionalStateId,
});

/** One colleague row (MYN-202, FR-175): mirrors the Backend `ColleagueContactDto`. */
const colleagueContactSchema = z.object({
  sectorId: z.preprocess(
    toNoValue,
    z
      .coerce.number({ error: COLLEAGUE_ERRORS.SECTOR_REQUIRED })
      .int()
      .positive(COLLEAGUE_ERRORS.SECTOR_REQUIRED),
  ),
  name: z
    .string()
    .trim()
    .min(1, COLLEAGUE_ERRORS.NAME_REQUIRED)
    .max(COLLEAGUE_NAME_MAX_LENGTH, COLLEAGUE_ERRORS.NAME_TOO_LONG),
  email: z
    .string()
    .trim()
    .min(1, COLLEAGUE_ERRORS.EMAIL_REQUIRED)
    .email(COLLEAGUE_ERRORS.EMAIL_INVALID)
    .max(COLLEAGUE_EMAIL_MAX_LENGTH, COLLEAGUE_ERRORS.EMAIL_TOO_LONG),
});

/**
 * Investor profile edit-mode form (US-040/US-101). FORMAT-ONLY (MYN-278, FR-186,
 * A-203): every field is optional and a profile saves with whatever is filled in.
 * Which fields still count toward matching is the Backend's call
 * (`matchingMissingFields`), never decided here. A value that IS filled in must be
 * well formed; the Backend re-validates everything regardless.
 */
export const investorProfileSchema = z.object({
  firmName: optionalText,
  investorType: optionalSelect,
  contactName: optionalText,
  contactTitle: optionalSelect,
  // Optional, but a legacy free-text phone the user has not replaced is kept out of the
  // payload (`legacyPhone`, form-only; same pattern as the partner profile, MYN-277).
  phone: phoneSchema(false),
  legacyPhone: z.string().optional(),
  websiteUrl: optionalUrl,
  linkedinUrl: optionalUrl,
  // WRITE fields: the raw S3 keys the upload flow returns (MYN-175). The
  // matching READ fields are `data.logoUrl` / `data.profilePhotoUrl`, which the
  // backend has already resolved to presigned URLs - never send those back.
  // `UpdateInvestorProfileDto` has no `profilePhotoUrl` at all, and the API
  // runs `forbidNonWhitelisted`, so sending it rejected the ENTIRE request.
  logoFileKey: optionalText,
  profilePhotoFileKey: optionalText,
  intro: optionalText,
  addressStreet: optionalText,
  addressStreet2: optionalText,
  addressCity: optionalText,
  // Country and State are dropdowns that store the chosen NAMES (A-204).
  addressState: optionalText,
  addressPostalCode: optionalText,
  addressCountry: optionalText,
  // Whole, non-negative amounts (MYN-277): digits in, number out, blank is "not filled in".
  prefAmountLow: optionalWholeNumber,
  prefAmountHigh: optionalWholeNumber,
  prefMinRevenue: optionalSelect,
  prefRecurringRevenue: optionalSelect,
  prefFemaleLed: choice(INVESTOR_PREF_LEVEL_OPTIONS),
  prefUnderrepresented: choice(INVESTOR_PREF_LEVEL_OPTIONS),
  prefUnderrepresentedCategories: z.array(z.string()).optional(),
  prefB2b: choice(MARKET_MODEL_OPTIONS),
  prefB2c: choice(MARKET_MODEL_OPTIONS),
  prefB2g: choice(MARKET_MODEL_OPTIONS),
  prefSaas: choice(MARKET_MODEL_OPTIONS),
  prefAi: choice(MARKET_MODEL_OPTIONS),
  prefLeadOrFollow: optionalSelect,
  activelyDeploying: z.boolean().optional(),
  prefOtherCriteria: optionalText,
  interestOfficeHours: z.boolean().optional(),
  interestFounderFunderEvents: z.boolean().optional(),
  // The three sets may be empty (an empty list clears the set, FR-186).
  industries: z
    .array(z.string())
    .max(MAX_INVESTOR_INDUSTRIES, E.INDUSTRIES_MAX(MAX_INVESTOR_INDUSTRIES))
    .default([]),
  geographies: z.array(geographySchema).default([]),
  preferredStages: z.array(z.string()).default([]),
  colleagueContacts: z.array(colleagueContactSchema).default([]),
}).superRefine((vals, ctx) => {
  // One colleague per sector (A-183). The section also hides a sector already
  // used in another row; this is the backstop, and it flags the LATER row, the
  // same one the Backend names.
  const seen = new Set<number>();
  vals.colleagueContacts.forEach((contact, index) => {
    if (seen.has(contact.sectorId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: COLLEAGUE_ERRORS.COLLEAGUE_CONTACT_DUPLICATE_SECTOR,
        path: ['colleagueContacts', index, 'sectorId'],
      });
    }
    seen.add(contact.sectorId);
  });
  // The range is only checked when BOTH amounts are filled in.
  const { prefAmountLow: low, prefAmountHigh: high } = vals;
  if (low !== undefined && high !== undefined && high < low) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: E.AMOUNT_RANGE,
      path: ['prefAmountHigh'],
    });
  }
});

export type InvestorProfileFormValues = z.infer<typeof investorProfileSchema>;

/**
 * A stored Decimal (a string on the wire) as a whole number of dollars; blank is "not
 * filled in". The column holds cents, but the field takes whole dollars, so a saved
 * `1500.25` opens as 1,500 (rounded, never with its dot dropped) and saves as 1500.
 */
function toAmount(value: string | number | null | undefined): number | undefined {
  if (value == null || value === '') return undefined;
  const amount = Math.round(Number(value));
  return Number.isFinite(amount) ? amount : undefined;
}

/** Maps the fetched profile onto the form's default values. */
export function toFormValues(data: InvestorProfileData): InvestorProfileFormValues {
  return {
    firmName: data.firmName ?? '',
    investorType: data.investorType ?? '',
    contactName: data.contactName ?? '',
    contactTitle: data.contactTitle ?? '',
    // `phone` is a valid E.164 number or empty; free text saved before the picker existed
    // stays in `legacyPhone` and is shown beside the field until replaced (MYN-277).
    ...toPhoneFormFields(data.phone),
    websiteUrl: data.websiteUrl ?? '',
    linkedinUrl: data.linkedinUrl ?? '',
    // Deliberately NOT seeded from `data.logoUrl` / `data.profilePhotoUrl`:
    // those are resolved presigned URLs, and echoing one back as a fileKey
    // fails the backend's `@Matches` pattern. Empty means "leave the stored
    // key alone"; the upload control fills it only when a new file is picked.
    logoFileKey: '',
    profilePhotoFileKey: '',
    intro: data.intro ?? '',
    addressStreet: data.addressStreet ?? '',
    addressStreet2: data.addressStreet2 ?? '',
    addressCity: data.addressCity ?? '',
    addressState: data.addressState ?? '',
    addressPostalCode: data.addressPostalCode ?? '',
    addressCountry: data.addressCountry ?? '',
    prefAmountLow: toAmount(data.prefAmountLow),
    prefAmountHigh: toAmount(data.prefAmountHigh),
    prefMinRevenue: data.prefMinRevenue ?? '',
    prefRecurringRevenue: data.prefRecurringRevenue ?? '',
    prefFemaleLed: data.prefFemaleLed ?? undefined,
    prefUnderrepresented: data.prefUnderrepresented ?? undefined,
    prefUnderrepresentedCategories: data.prefUnderrepresentedCategories ?? [],
    prefB2b: data.prefB2b ?? undefined,
    prefB2c: data.prefB2c ?? undefined,
    prefB2g: data.prefB2g ?? undefined,
    prefSaas: data.prefSaas ?? undefined,
    prefAi: data.prefAi ?? undefined,
    prefLeadOrFollow: data.prefLeadOrFollow ?? '',
    activelyDeploying: data.activelyDeploying ?? false,
    prefOtherCriteria: data.prefOtherCriteria ?? '',
    interestOfficeHours: data.interestOfficeHours ?? false,
    interestFounderFunderEvents: data.interestFounderFunderEvents ?? false,
    industries: data.industries ?? [],
    // Pick only the fields the form/API know about - the GET response's
    // geography rows carry DB metadata (`id`, `investorProfileId`) that
    // `GeographyPreferenceDto` doesn't whitelist; spreading the row through
    // unchanged used to smuggle those fields into the save payload and trip
    // the backend's `forbidNonWhitelisted` validation on every save.
    geographies:
      data.geographies?.map((g) => ({
        worldRegion: g.worldRegion,
        countryId: g.countryId,
        stateId: g.stateId ?? undefined,
      })) ?? [],
    preferredStages: data.preferredStages ?? [],
    // Same trap as geographies: the GET rows carry an `id` the PATCH DTO does
    // not whitelist, so pick only the writable fields (MYN-202).
    colleagueContacts:
      data.colleagueContacts?.map(({ sectorId, name, email }) => ({ sectorId, name, email })) ?? [],
  };
}

/**
 * Optional columns that accept `null` as "clear" on the Backend (nullable columns only:
 * `firmName`, the booleans and the lists are not among them, and `null` there is ignored).
 */
const CLEARABLE_KEYS = [
  'investorType',
  'contactName',
  'contactTitle',
  'websiteUrl',
  'linkedinUrl',
  'intro',
  'addressStreet',
  'addressStreet2',
  'addressCity',
  'addressState',
  'addressPostalCode',
  'addressCountry',
  'prefAmountLow',
  'prefAmountHigh',
  'prefMinRevenue',
  'prefRecurringRevenue',
  'prefFemaleLed',
  'prefUnderrepresented',
  'prefB2b',
  'prefB2c',
  'prefB2g',
  'prefSaas',
  'prefAi',
  'prefLeadOrFollow',
  'prefOtherCriteria',
] as const satisfies readonly (keyof InvestorProfileFormValues & keyof InvestorProfileData)[];

function isFilled(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '' && !Number.isNaN(value);
}

/**
 * Maps form values back onto the API payload shape for PATCH.
 *
 * `saved` is the profile as last read. A previously saved optional value the user has
 * emptied is sent as `null`, which the Backend reads as "clear it"; an empty value that
 * was never saved is left out (the Backend reads `''` as "not sent", so it would do
 * nothing). A legacy free-text phone is kept unless the user removed it.
 */
export function toApiPayload(
  values: InvestorProfileFormValues,
  saved?: InvestorProfileData,
): InvestorProfileData {
  const { logoFileKey, profilePhotoFileKey, phone, legacyPhone, ...rest } = values;
  const payload: InvestorProfileData = { ...rest };
  for (const key of CLEARABLE_KEYS) {
    if (isFilled(values[key])) continue;
    delete payload[key];
    if (isFilled(saved?.[key])) (payload as Record<string, unknown>)[key] = null;
  }
  const phoneChange = phone?.trim()
    ? { phone }
    : !legacyPhone?.trim() && isFilled(saved?.phone)
      ? { phone: null }
      : {};
  return {
    ...payload,
    ...phoneChange,
    geographies: values.geographies.map((g) => ({ ...g, stateId: g.stateId ?? null })),
    // Omitted entirely unless the upload control actually set one. On the
    // backend `''` is the deliberate "clear it" signal, NOT "unchanged", so
    // spreading the form's empty default would wipe the stored logo and photo
    // of anyone who edited their address and never touched an image.
    ...(logoFileKey ? { logoFileKey } : {}),
    ...(profilePhotoFileKey ? { profilePhotoFileKey } : {}),
  };
}
